import type { KpiSignal } from './types';
import { KPI_DEFAULTS } from './defaults';

export type SummaryWeights = {
  schedule: number;
  finance: number;
  mission: number;
  data_reliability: number;
};

export type SummaryConfig = {
  weights: SummaryWeights;
  include_reliability: boolean;
};

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function colorFromScore(score: number | null): number {
  // Use the same band thresholds as Mission/Reliability so the rolled-up
  // summary color matches the components feeding it. Centralized in
  // KPI_DEFAULTS so a single edit shifts the bands platform-wide.
  if (score === null || !Number.isFinite(score)) return 0;
  if (score >= KPI_DEFAULTS.mission.bands.green_threshold) return 3;
  if (score >= KPI_DEFAULTS.mission.bands.yellow_threshold) return 2;
  return 1;
}

function trendFromDelta(delta: number | null, eps: number = 1): -1 | 0 | 1 {
  if (delta === null || !Number.isFinite(delta)) return 0;
  if (delta > eps) return 1;
  if (delta < -eps) return -1;
  return 0;
}

/**
 * For KPIs that are not yet fully implemented (e.g., Finance), we can derive a rough score
 * from the existing UI color value so Summary isn't empty.
 */
export function scoreFromExistingColor(colorValue: number | null | undefined): number | null {
  if (colorValue === null || colorValue === undefined || !Number.isFinite(colorValue as any)) return null;
  const v = Number(colorValue);
  // Back-compat: some legacy data uses a broader scale (e.g., 1..4).
  // We map it onto the standard 0..100 score bands for Summary roll-ups.
  // Discrete preferred: 1=red, 2=yellow, 3=green.
  if (v >= 3.5) return 90;   // green-ish
  if (v >= 2.5) return 70;   // yellow-ish
  if (v >= 1.0) return 50;   // red-ish
  return null;
}

export function computeSummaryKpiSignal(
  asOfDate: string,
  cfg: SummaryConfig,
  inputs: {
    schedule?: { score: number | null; trend?: -1 | 0 | 1 };
    finance?: { score: number | null; trend?: -1 | 0 | 1 };
    mission?: { score: number | null; trend?: -1 | 0 | 1 };
    data_reliability?: { score: number | null; trend?: -1 | 0 | 1 };
  }
): KpiSignal {
  const weights = { ...cfg.weights };
  if (!cfg.include_reliability) weights.data_reliability = 0;

  const items: Array<{ key: keyof SummaryWeights; score: number | null; trend: -1 | 0 | 1 } > = [
    { key: 'schedule', score: inputs.schedule?.score ?? null, trend: inputs.schedule?.trend ?? 0 },
    { key: 'finance', score: inputs.finance?.score ?? null, trend: inputs.finance?.trend ?? 0 },
    { key: 'mission', score: inputs.mission?.score ?? null, trend: inputs.mission?.trend ?? 0 },
    { key: 'data_reliability', score: inputs.data_reliability?.score ?? null, trend: inputs.data_reliability?.trend ?? 0 },
  ];

  // Exclude missing scores and renormalize weights among those that remain.
  const included = items.filter((i) => i.score !== null && weights[i.key] > 0);
  const wsum = included.reduce((acc, i) => acc + weights[i.key], 0);
  if (included.length === 0 || wsum <= 0) {
    return {
      score_0_100: null,
      indicator_color_value: 0,
      indicator_trend_value: 0,
      as_of_date: asOfDate,
      explain: { headline: 'Summary score unavailable (insufficient KPI inputs).' },
    };
  }

  let score = 0;
  let trendAgg = 0;
  for (const i of included) {
    const w = weights[i.key] / wsum;
    score += (i.score ?? 0) * w;
    trendAgg += (i.trend ?? 0) * w;
  }

  const s = Math.round(clamp(score, 0, 100));
  const t = trendFromDelta(trendAgg, 0.05);

  return {
    score_0_100: s,
    indicator_color_value: colorFromScore(s),
    indicator_trend_value: t,
    as_of_date: asOfDate,
    explain: {
      headline: `Summary score ${s}/100 based on ${included.length} KPI(s).`,
      details: {
        included: included.map((i) => ({ key: i.key, score: i.score, weight: weights[i.key] })),
      },
    },
  };
}
